import {Injectable, NgZone, TemplateRef} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {MatStepper} from '@angular/material';
import {Step} from './step/step';
import {StepperConfig} from './stepper.config';
import {StepComponent, StepData} from './step/step.component';

@Injectable()
export class StepperService {

    private currentStepSubject$ = new BehaviorSubject<Step>(null);
    public currentStep$ = this.currentStepSubject$.asObservable();

    private stepsSubject$ = new BehaviorSubject<Step[]>([]);
    public steps$ = this.stepsSubject$.asObservable();

    private stepMap = new Map<string, Step>();

    private stepperConfig: StepperConfig;

    private stepper: MatStepper;

    constructor(private readonly zone: NgZone) {
    }

    public get currStep() {
        return this.currentStepSubject$.value;
    }

    public init(stepperConfig: StepperConfig, stepper: MatStepper, typeTemplatesMap: Map<string, TemplateRef<StepComponent>>) {
        this.stepper = stepper;
        this.stepperConfig = stepperConfig;

        const fieldWrappers = [];
        stepperConfig.steps.forEach(stepConfig => {
            const step = new Step({...stepConfig});
            step.template = typeTemplatesMap.get(stepConfig.name);
            step.index = fieldWrappers.length;

            this.stepMap.set(stepConfig.name, step);
            fieldWrappers.push(step);
        });

        this.stepsSubject$.next(fieldWrappers);
        this.currentStepSubject$.next(fieldWrappers[0]);

        this.currStep.initialized = true;
    }

    public getStepByName(stepName: string): Step {
        return this.stepMap.get(stepName);
    }

    public getStepByIndex(index: number): Step {
        return this.stepsSubject$.value[index];
    }

    public async nextStep(stepName: string = null) {
        const stepData = (await this.currStep.component.validate() as StepData);
        this.setStepData(this.currStep, stepData);

        if (stepData.hasOwnProperty('allowNext') && !stepData.allowNext) {
            return;
        }
        if (!stepData.valid) {
            return;
        }
        if (!stepName && this.stepsSubject$.value.length - 1 === this.stepper.selectedIndex) {
            return;
        }

        let nextIndex = this.stepper.selectedIndex;
        let nextStep: Step= null;
        if (stepName) {
            if (this.currStep.config.name === stepName) {
                return;
            }
            nextIndex = -1;
            for (let i = 0; i < this.stepsSubject$.value.length; i++) {
                if (!this.stepsSubject$.value[i].hidden) {
                    nextIndex ++;
                }
                if (this.stepsSubject$.value[i].config.name === stepName) {
                    nextStep = this.stepsSubject$.value[i]
                    break;
                }
            }
            nextIndex = nextStep ? nextIndex : this.stepper.selectedIndex;
        } else {
            nextIndex = this.stepper.selectedIndex;
            for (let i = this.stepper.selectedIndex + 1; i < this.stepsSubject$.value.length; i++) {
                if (!this.stepsSubject$.value[i].hidden) {
                    nextIndex ++;
                    nextStep = this.stepsSubject$.value[i];
                    break;
                }
            }
            nextIndex = nextStep ? nextIndex : this.stepper.selectedIndex;
        }


        setTimeout(() => {
            this.stepper.selectedIndex = nextIndex;
            if (this.stepper.selectedIndex !== nextIndex) {
                return;
            }
            this.zone.run(() =>{
                 this.currentStepSubject$.next(nextStep);
                 this.currStep.initialized = true;
            });

            
            setTimeout(() => {
                this.zone.run(() => this.currStep.component.enter());
            });

        });
    }

    public async prevStep(stepName: string = null) {
        if (!stepName && this.stepper.selectedIndex === 0) {
            return;
        }
        if (stepName && this.currStep.config.name === stepName) {
            return;
        }

        let index;
        let foundPrevItem = false;
        for (index = this.stepper.selectedIndex - 1; index >= 0; index--) {
            if (this.stepsSubject$.value[index].config.name === stepName) {
                foundPrevItem = true;

                break;
            }
            if (!stepName) {
                if (this.stepsSubject$.value[index].config.editable) {
                    foundPrevItem = true;

                    break;
                }
            }
        }

        if (!foundPrevItem) {
            return;
        }

        this.stepper.selectedIndex = index;
        this.currentStepSubject$.next(this.stepsSubject$.value[index]);

        this.currStep.initialized = true;

        setTimeout(() => {
            this.currStep.component.enter();
        });
    }

    public setStepData(step: Step, stepData: StepData, configValidation = true) {
        if (stepData.error!= undefined) {
            step.error = stepData.error;
        }
        if (stepData.valid!= undefined) {
            step.completed = stepData.valid;
        }
        
        if (stepData.editable!= undefined) {
            step.editable = stepData.editable;
        }
        if (stepData.enabled!= undefined) {
            step.enabled = stepData.enabled;
        }
        if (stepData.optional!= undefined) {
            step.optional = stepData.optional;
        }

        if (stepData.hidden!= undefined) {
            step.hidden = stepData.hidden;
        }

        if (stepData.data!= undefined) {
            step.data = stepData.data;
        }

        this.checkSteps();

        if (!configValidation) {
            return;
        }
        if (this.stepperConfig?.validate) {
            this.stepperConfig.validate(this.currStep);
        }
    }

    private checkSteps() {
        let prevStepCompleted = this.stepsSubject$.value[0].completed;
        this.stepsSubject$.value.forEach(step => {
            step.enabled = prevStepCompleted;

            prevStepCompleted = step.completed;
        });
    }
}
